import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useLocation, useNavigate } from "react-router-dom";
import { PageHeader, KPI, Card, Button, Input, Select, Textarea, Label, Empty } from "@/components/Primitives";
import { StatusPill, leadStageVariant } from "@/components/StatusPill";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Filter, Search, Trash2, MapPin, ChevronRight, Users } from "lucide-react";
import { toast } from "sonner";

function gbp(v) {
  const n = v ?? 0;
  const hasPence = Math.round(n * 100) !== Math.round(n) * 100;
  return `£${n.toLocaleString(undefined, { minimumFractionDigits: hasPence ? 2 : 0, maximumFractionDigits: 2 })}`;
}

const STAGES = ["New", "Qualified", "Proposal", "Won", "Lost"];
const INDUSTRIES = ["Sports", "Retail", "Finance", "Media", "Education", "SaaS", "Other"];
const SOURCES = ["Website", "Referral", "Outbound", "Event", "Partner", "Other"];

function leadStageClass(s) {
  switch (s) {
    case "New": return "bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))] border-[hsl(var(--primary))]/20";
    case "Qualified": return "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20";
    case "Proposal": return "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20";
    case "Won": return "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20";
    case "Lost": return "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20";
    default: return "bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))] border-[hsl(var(--border))]";
  }
}

export default function LeadsPage() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("All");
  const [showNew, setShowNew] = useState(false);
  const [referralsOnly, setReferralsOnly] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();
  const isOwner = user?.role === "admin";

  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    if (params.get("new") === "1") setShowNew(true);
    if (params.get("source") === "referral") setReferralsOnly(true);
  }, [loc.search]);

  const load = async () => {
    const { data } = await api.get("/leads");
    setLeads(data || []);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      if (stage !== "All" && l.stage !== stage) return false;
      if (referralsOnly && l.source !== "Soubor One · interest form") return false;
      if (q && !(`${l.company} ${l.contact}`.toLowerCase().includes(q.toLowerCase()))) return false;
      return true;
    });
  }, [leads, q, stage, referralsOnly]);

  const referralCount = useMemo(
    () => leads.filter((l) => l.source === "Soubor One · interest form").length,
    [leads]
  );

  const pipelineValue = useMemo(
    () => leads.filter((l) => !["Won", "Lost"].includes(l.stage)).reduce((s, l) => s + l.est_value, 0),
    [leads]
  );
  const open = leads.filter((l) => !["Won", "Lost"].includes(l.stage)).length;
  const won = leads.filter((l) => l.stage === "Won").length;
  const lost = leads.filter((l) => l.stage === "Lost").length;
  const winRate = won + lost > 0 ? Math.round((won / (won + lost)) * 100) : 0;

  const changeStage = async (l, newStage) => {
    await api.patch(`/leads/${l.id}`, { stage: newStage });
    load();
  };

  const deleteLead = async (l, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete lead "${l.company}"? This removes it from Firestore too.`)) return;
    try {
      await api.delete(`/leads/${l.id}`);
      toast.success("Lead deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="Sales · Leads"
        title="Leads & pipeline"
        subtitle="Every opportunity, ranked by value and likelihood."
        actions={
          <>
            <Button variant="secondary" onClick={() => setStage("All")}><Filter className="w-4 h-4" strokeWidth={1.6}/>All</Button>
            <Button onClick={() => setShowNew(true)} data-testid="new-lead-btn"><Plus className="w-4 h-4" strokeWidth={2}/>New lead</Button>
          </>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Pipeline value" value={gbp(pipelineValue)} variant="primary" />
        <KPI label="Open leads" value={open} />
        <KPI label="Win rate (all-time)" value={`${winRate}%`} variant="success" />
        <KPI label="Lost" value={lost} variant={lost > 0 ? "danger" : "default"} />
      </div>

      <Card>
        <div className="px-5 py-3 flex flex-wrap items-center gap-3 border-b border-[hsl(var(--border))]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--text-muted))]" strokeWidth={1.6}/>
            <Input placeholder="Search company or contact…" className="pl-9 w-[280px]" value={q} onChange={(e) => setQ(e.target.value)} data-testid="lead-search"/>
          </div>
          <Select value={stage} onChange={(e) => setStage(e.target.value)} className="w-[160px]" data-testid="stage-filter">
            <option value="All">All stages</option>
            {STAGES.map((s) => <option key={s}>{s}</option>)}
          </Select>
          <button
            type="button"
            onClick={() => setReferralsOnly((v) => !v)}
            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-[12.5px] font-semibold transition-colors ${
              referralsOnly
                ? "bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30"
                : "bg-[hsl(var(--surface))] text-[hsl(var(--text-muted))] border-[hsl(var(--border))] hover:text-[hsl(var(--text))]"
            }`}
            data-testid="referrals-filter-chip"
          >
            <Users className="w-3.5 h-3.5" strokeWidth={2}/>
            Referrals only
            {referralCount > 0 && (
              <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${referralsOnly ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))]"}`}>{referralCount}</span>
            )}
          </button>
          <div className="ml-auto text-[12px] text-[hsl(var(--text-muted))]">{filtered.length} of {leads.length}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))]">
                <th className="text-left font-semibold px-5 py-3">Company</th>
                <th className="text-left font-semibold px-5 py-3">Contact</th>
                <th className="text-left font-semibold px-5 py-3">Industry</th>
                <th className="text-left font-semibold px-5 py-3">Stage</th>
                <th className="text-left font-semibold px-5 py-3">Owner</th>
                <th className="text-right font-semibold px-5 py-3">Value</th>
                <th className="text-right font-semibold px-5 py-3">Prob.</th>
                <th className="text-left font-semibold px-5 py-3">Next action</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr
                  key={l.id}
                  className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-hover))] cursor-pointer group"
                  onClick={() => nav(`/leads/${l.id}`)}
                  data-testid="lead-row"
                >
                  <td className="px-5 py-3 font-medium">
                    <div className="flex items-center gap-2">
                      {l.company}
                      {(l.address || l.lat) && <MapPin className="w-3 h-3 text-[hsl(var(--primary))]" strokeWidth={2}/>}
                    </div>
                  </td>
                  <td className="px-5 py-3">{l.contact} {l.position && <span className="text-[hsl(var(--text-muted))]">· {l.position}</span>}</td>
                  <td className="px-5 py-3 text-[hsl(var(--text-muted))]">{l.industry || "—"}</td>
                  <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={l.stage}
                      onChange={(e) => changeStage(l, e.target.value)}
                      className={`text-[11px] font-semibold cursor-pointer focus:outline-none rounded-full px-2.5 py-1 border ${leadStageClass(l.stage)}`}
                      data-testid="stage-select"
                    >
                      {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-5 py-3">{l.owner}</td>
                  <td className="px-5 py-3 text-right font-mono font-semibold">{gbp(l.est_value)}</td>
                  <td className="px-5 py-3 text-right font-mono">{l.win_likelihood}%</td>
                  <td className="px-5 py-3 text-[hsl(var(--text-muted))]">{l.next_action || "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-1 opacity-50 group-hover:opacity-100 transition-opacity">
                      {isOwner && (
                        <button onClick={(e) => deleteLead(l, e)} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))] hover:text-rose-600" title="Delete" data-testid="delete-lead-btn">
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6}/>
                        </button>
                      )}
                      <ChevronRight className="w-4 h-4 text-[hsl(var(--text-muted))]" strokeWidth={1.6}/>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9}><Empty title="No leads found" subtitle="Try clearing filters or create your first lead." /></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {showNew && <NewLeadModal onClose={() => { setShowNew(false); nav("/leads", { replace: true }); }} onCreated={load} />}
    </div>
  );
}

function NewLeadModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    company: "", contact: "", position: "", email: "", phone: "",
    industry: "Sports", source: "Website", priority: "Normal", owner: "Josh",
    est_value: 5000, win_likelihood: 50, stage: "New", next_action: "", notes: "",
    address: "", website: "",
  });
  const [saving, setSaving] = useState(false);
  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/leads", { ...form, est_value: Number(form.est_value), win_likelihood: Number(form.win_likelihood) });
      await onCreated();
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-end bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="h-full w-[min(560px,100vw)] bg-[hsl(var(--surface))] border-l border-[hsl(var(--border))] shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="new-lead-modal">
        <div className="sticky top-0 bg-[hsl(var(--surface))] border-b border-[hsl(var(--border))] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))]">New</div>
            <h2 className="text-[18px] font-bold">Lead / Prospect</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-[hsl(var(--surface-hover))]"><X className="w-4 h-4" strokeWidth={1.6}/></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Company*</Label><Input required value={form.company} onChange={f("company")} placeholder="Company Ltd" data-testid="lead-company"/></div>
            <div><Label>Industry</Label><Select value={form.industry} onChange={f("industry")}>{INDUSTRIES.map((i) => <option key={i}>{i}</option>)}</Select></div>
            <div><Label>Main contact*</Label><Input required value={form.contact} onChange={f("contact")} placeholder="Jane Doe"/></div>
            <div><Label>Position</Label><Input value={form.position} onChange={f("position")} placeholder="CTO / Head of Ops"/></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={f("email")} placeholder="jane@company.com"/></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={f("phone")} placeholder="+44 7…"/></div>
            <div><Label>Source</Label><Select value={form.source} onChange={f("source")}>{SOURCES.map((i) => <option key={i}>{i}</option>)}</Select></div>
            <div><Label>Priority</Label><Select value={form.priority} onChange={f("priority")}><option>Low</option><option>Normal</option><option>High</option></Select></div>
            <div><Label>Owner</Label><Input value={form.owner} onChange={f("owner")}/></div>
            <div><Label>Stage</Label><Select value={form.stage} onChange={f("stage")}>{STAGES.map((s) => <option key={s}>{s}</option>)}</Select></div>
            <div><Label>Est. value (ex VAT)</Label><Input type="number" value={form.est_value} onChange={f("est_value")}/></div>
            <div>
              <div className="flex justify-between"><Label>Win likelihood</Label><span className="text-[11px] font-mono">{form.win_likelihood}%</span></div>
              <input type="range" min="0" max="100" step="5" value={form.win_likelihood} onChange={f("win_likelihood")} className="w-full accent-[hsl(var(--primary))] mt-2"/>
            </div>
          </div>
          <div><Label>Next action</Label><Input value={form.next_action} onChange={f("next_action")} placeholder="Call to qualify / Send proposal"/></div>
          <div><Label>Website</Label><Input value={form.website} onChange={f("website")} placeholder="https://company.com"/></div>
          <div><Label>Address (for travel planning)</Label><Input value={form.address} onChange={f("address")} placeholder="Street, City, Postcode. Used for Google Maps"/></div>
          <div><Label>Notes</Label><Textarea value={form.notes} onChange={f("notes")} placeholder="Context, requirements, objections…"/></div>
          <div className="flex items-center gap-2 pt-2">
            <Button type="submit" disabled={saving} data-testid="save-lead">{saving ? "Saving…" : "Save lead"}</Button>
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
